<template>
    <div class="absolute inset-0 z-[40] pointer-events-none">
        <!-- Floating Pencil Button (Bottom Right) -->
        <!-- <div class="flex flex-col gap-2 fixed bottom-2 right-2 z-[10]  md:bottom-6 md:right-10 w-10 h-fit">
            <Tooltip text="Add user notes" position="left">
                <button 
                    @click="addUserNotes"
                    class="relative z-[60] float-menu-icon-button p-3 rounded-full shadow-lg transition-all duration-300 hover:shadow-xl"
                    :class="{ 'bg-slate-800 dark:bg-gray-700': showControls }"
                >
                    <DocumentPlusIcon class="w-6 h-6" />
                </button>
            </Tooltip>
            <Tooltip text="Realtime drawing" position="left">
                <button 
                    @click="toggleControls"
                    class=" z-[60] float-menu-icon-button text-white p-3 rounded-full shadow-lg transition-all duration-300 hover:shadow-xl"
                    :class="{ 'bg-slate-800 dark:bg-gray-700': showControls }"
                >
                    <PencilIcon class="w-6 h-6" />
                </button>
            </Tooltip>
        </div> -->

        <div
            v-if="showControls"
            class="fixed select-none pointer-events-auto"
            style="z-index: 9999;"
            :style="{ top: toolbarPos.y + 'px', left: toolbarPos.x + 'px' }"
        >
            <div class="flex flex-row border border-slate-600 dark:border-gray-700 gap-1 rounded-md bg-slate-700 dark:bg-gray-800 text-white justify-center md:w-auto shadow-lg">
                <!-- Drag Handle -->
                <div
                    class="flex items-center px-2 cursor-grab active:cursor-grabbing touch-none text-gray-400 hover:text-gray-200 transition-colors"
                    title="Drag to move toolbar"
                    @pointerdown.stop="startToolbarDrag"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                        <circle cx="9" cy="6" r="1.5"/><circle cx="15" cy="6" r="1.5"/>
                        <circle cx="9" cy="12" r="1.5"/><circle cx="15" cy="12" r="1.5"/>
                        <circle cx="9" cy="18" r="1.5"/><circle cx="15" cy="18" r="1.5"/>
                    </svg>
                </div>

                <div class="flex flex-row items-center gap-1 p-2">
                    <!-- Connection Status -->
                    <div class="flex items-center gap-1 px-2">
                        <div 
                            class="w-2 h-2 rounded-full"
                            :class="isConnected ? 'bg-green-500' : 'bg-red-500'"
                        ></div>
                        <span class="text-xs text-gray-300">
                            {{ isConnected ? 'Connected' : 'Disconnected' }}
                        </span>
                    </div>
                    
                    <!-- Divider -->
                    <div class="w-px h-6 bg-slate-600 dark:bg-gray-600 mx-1"></div>
                    
                    <!-- Drawing Toggle -->
                    <Tooltip text="Toggle drawing" position="bottom">
                        <button 
                            @click="toggleDrawing"
                            class="px-2 py-1 text-xs rounded transition-all duration-200"
                            :class="isDrawingEnabled ? 'bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white' : 'text-gray-300 hover:text-white hover:bg-slate-600 dark:hover:bg-gray-700'"
                        >
                            {{ isDrawingEnabled ? 'Drawing ON' : 'Drawing OFF' }}
                        </button>
                    </Tooltip>
                    
                    <!-- Divider -->
                    <div class="w-px h-6 bg-slate-600 dark:bg-gray-600 mx-1"></div>
                    
                    <!-- Save Draw Toggle -->
                    <Tooltip text="Enable save drawing" position="bottom">
                        <button 
                            @click="toggleSaveDraw"
                            class="px-2 py-1 text-xs rounded transition-all duration-200"
                            :class="saveDrawEnabled ? 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white' : 'text-gray-300 hover:text-white hover:bg-slate-600 dark:hover:bg-gray-700'"
                        >
                            {{ saveDrawEnabled ? 'Save ON' : 'Save OFF' }}
                        </button>
                    </Tooltip>
                    
                    <!-- Divider -->
                    <div class="w-px h-6 bg-slate-600 dark:bg-gray-600 mx-1"></div>
                    
                    <!-- Clear Button -->
                    <Tooltip text="Clear drawing" position="bottom">
                        <button 
                            @click="clearCanvas"
                            class="px-2 py-1 text-xs rounded text-gray-300 hover:text-white hover:bg-slate-600 dark:hover:bg-gray-700 transition-all duration-200"
                        >
                            Clear
                        </button>
                    </Tooltip>
                </div>
            </div>
        </div>
        
        <!-- Drawing Settings Panel -->
        <div
            v-if="isDrawingEnabled && showControls"
            class="fixed pointer-events-auto"
            style="z-index: 9999;"
            :style="{ top: (toolbarPos.y + 48) + 'px', left: toolbarPos.x + 'px' }"
        >
            <div class="flex flex-row border border-slate-600 dark:border-gray-700 gap-2 rounded-md bg-slate-700 dark:bg-gray-800 text-white justify-center md:w-auto p-2 shadow-lg hover:bg-slate-800 hover:shadow-md">
                <div class="flex flex-row items-center gap-1">
                    <label class="text-xs text-gray-300 flex items-center">Brush Size:</label>
                    <input 
                        type="range" 
                        min="1" 
                        max="20" 
                        v-model="brushSize"
                        class="w-16 h-2 bg-slate-600 dark:bg-gray-600 rounded-lg cursor-pointer"
                    />
                </div>

                <div class="flex flex-row items-center gap-1">
                    <label class="text-xs text-gray-300 flex items-center">Color:</label>
                    <div>
                        <button
                            ref="colorPickerTrigger"
                            @click="toggleColorPicker"
                            class="w-8 h-6 rounded border border-slate-600 dark:border-gray-600 cursor-pointer"
                            :style="{ backgroundColor: brushColor }"
                            type="button"
                        ></button>
                        <ColorPicker
                            ref="colorPicker"
                            :defaultPosition="{ left: 15, top: 50 }"
                            :trigger-element="colorPickerTrigger"
                            @applyColor="handleColorChange"
                        />
                    </div>
                </div>
            </div>
        </div>

        <canvas 
            ref="canvas" 
            class="absolute top-0 left-0 w-full h-full"
            :class="{ 'cursor-crosshair pointer-events-auto': isDrawingEnabled, 'pointer-events-none': !isDrawingEnabled || isToolbarDragging }"
            style="z-index: 40; touch-action: none;"
            :style="{ zoom: zoomValue }"
            @mousedown="startDrawing"
            @mousemove="draw"
            @mouseup="stopDrawing"
            @mouseleave="stopDrawing"
            @touchstart="handleTouchStart"
            @touchmove="handleTouchMove"
            @touchend="handleTouchEnd"
            @touchcancel="handleTouchEnd"
        />
    </div>
</template>

<script setup>
/* eslint-disable */
import { ref, onMounted, onUnmounted, nextTick, watch, defineProps, defineEmits, defineExpose } from 'vue'
import drawingStore from '@/store/drawing.store'
import appStore from '@/store/app.store'
import { realtimeService } from '@/service/realtime.service'
// import { PencilIcon } from '@heroicons/vue/24/outline'
// import { DocumentPlusIcon } from '@heroicons/vue/24/solid'
import ColorPicker from '@/components/common/elements/ColorPicker.vue'
import { isMobile } from '@/service/utility'
import syncService from '@/service/sync.service'
import dbService from '@/service/db.service'
import userStore from '@/store/user.store'

// Props
const props = defineProps({
    enabled: {
        type: Boolean,
        default: false
    },
    showControls: {
        type: Boolean,
        default: false
    },
    brushSize: {
        type: Number,
        default: 3
    },
    brushColor: {
        type: String,
        default: '#000000'
    },
    targetSelector: {
        type: String,
        default: '#lyric-container'
    },
    fadeOutDuration: {
        type: Number,
        default: 5000 // milliseconds
    },
    roomId: {
        type: String,
        required: true
    },
    userId: {
        type: String,
        required: true
    },
    playlistId: {
        type: String,
        required: true
    },
    songId: {
        type: String,
        required: true
    },
    zoomValue: {
        type: Number,
        required: false,
        default: 1
    },
    contentReady: {
        type: Boolean,
        default: true
    },
})

// Emits
const emits = defineEmits(['drawingEnabled', 'drawingDisabled', 'canvasCleared', 'controlsToggled', 'saveDrawToggled'])

// Refs
const canvas = ref(null)
const ctx = ref(null)
const isDrawing = ref(false)
const isDrawingEnabled = ref(props.enabled)
const showControls = ref(props.showControls)
const brushSize = ref(props.brushSize)
const brushColor = ref(props.brushColor)
const lastX = ref(0)
const lastY = ref(0)
const colorPicker = ref(null)
const colorPickerTrigger = ref(null)

// Toolbar drag state
const toolbarPos = ref({ x: window.innerWidth - 340, y: 16 })
const toolbarDragOffset = ref({ x: 0, y: 0 })
const isToolbarDragging = ref(false)

const startToolbarDrag = (e) => {
    e.preventDefault()
    isToolbarDragging.value = true
    toolbarDragOffset.value = {
        x: e.clientX - toolbarPos.value.x,
        y: e.clientY - toolbarPos.value.y
    }
    const onMove = (ev) => {
        if (!isToolbarDragging.value) return
        const cx = ev.clientX ?? (ev.touches && ev.touches[0]?.clientX)
        const cy = ev.clientY ?? (ev.touches && ev.touches[0]?.clientY)
        if (cx == null || cy == null) return
        toolbarPos.value = {
            x: Math.max(0, Math.min(window.innerWidth - 60, cx - toolbarDragOffset.value.x)),
            y: Math.max(0, Math.min(window.innerHeight - 40, cy - toolbarDragOffset.value.y))
        }
    }
    const onUp = () => {
        isToolbarDragging.value = false
        window.removeEventListener('pointermove', onMove)
        window.removeEventListener('pointerup', onUp)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
}

// Save draw functionality
const saveDrawEnabled = ref(false)
const savedDrawingData = ref(null)
const drawingLoaded = ref(false)

// Generate storage key scoped to the active user
const getStorageKey = () => {
    let uId = props.userId
    if (!uId || uId.startsWith('anonymous-')) {
        uId = userStore?.state?.userInfo?.id ? `user-${userStore.state.userInfo.id}` : 'user-anon'
    }
    return `saved-drawing-${uId}-${props.playlistId}-${props.songId}`
}

const getLegacyStorageKey = () => {
    return `saved-drawing-${props.playlistId}-${props.songId}`
}

// Fade-out functionality
const drawnStrokes = ref([])
const fadeAnimationId = ref(null)
const fadeOutDuration = ref(props.fadeOutDuration)

// Realtime functionality
const channel = ref(null)
const isConnected = ref(false)

// Setup Ably realtime channel
const setupRealtimeChannel = () => {
    if (!props.roomId || !props.userId) {
        console.warn('Room ID and User ID are required for realtime functionality')
        return
    }

    // Create channel for this room and subscribe
    channel.value = realtimeService.subscribe(
        `drawing-room-${props.roomId}`,
        '*',
        (payload) => {
            if (payload.userId === props.userId) return
            if (!appStore.state.enabledRealtime) return
            
            if (payload.event === 'drawing-stroke') {
                handleRemoteStroke(payload)
            } else if (payload.event === 'drawing-clear') {
                handleRemoteClear()
            }
        },
        (status) => {
            isConnected.value = status === 'SUBSCRIBED'
        }
    )
}

// Handle incoming stroke from other users
const handleRemoteStroke = (strokeData) => {
    if (!ctx.value) return
    
    const { points, brushSize: remoteBrushSize, brushColor: remoteBrushColor, userId: remoteUserId } = strokeData
    
    // Create stroke object for remote drawing
    const remoteStroke = {
        points: points,
        brushSize: remoteBrushSize,
        brushColor: remoteBrushColor,
        opacity: 1,
        startTime: Date.now(),
        userId: remoteUserId,
        isRemote: true
    }
    
    // Add to drawn strokes
    drawnStrokes.value.push(remoteStroke)
    
    // Draw the stroke immediately
    if (points.length > 1) {
        ctx.value.beginPath()
        ctx.value.strokeStyle = remoteBrushColor
        ctx.value.lineWidth = remoteBrushSize
        ctx.value.globalAlpha = 1
        
        ctx.value.moveTo(points[0].x, points[0].y)
        for (let i = 1; i < points.length; i++) {
            ctx.value.lineTo(points[i].x, points[i].y)
        }
        ctx.value.stroke()
        
        // Reset global alpha
        ctx.value.globalAlpha = 1
    }
    
    // Start fade animation for remote stroke
    startFadeAnimation()
}

// Handle incoming clear event from other users
const handleRemoteClear = () => {
    if (!ctx.value) return
    
    ctx.value.clearRect(0, 0, canvas.value.width, canvas.value.height)
    drawnStrokes.value = []
    if (fadeAnimationId.value) {
        cancelAnimationFrame(fadeAnimationId.value)
        fadeAnimationId.value = null
    }
}

// Send stroke data to other users
const sendStrokeToRealtime = (strokeData) => {
    if (!appStore.state.enabledRealtime || !channel.value || !isConnected.value || saveDrawEnabled.value || strokeData.saved) return
    
    channel.value.publish('drawing-stroke', {
        ...strokeData,
        userId: props.userId,
        timestamp: Date.now()
    })
}

// Initialize canvas
const initCanvas = () => {
    if (!canvas.value) return
    
    ctx.value = canvas.value.getContext('2d')
    resizeCanvas()
    setupCanvas()
}

// Setup canvas properties
const setupCanvas = () => {
    if (!ctx.value) return
    
    ctx.value.lineCap = 'round'
    ctx.value.lineJoin = 'round'
    ctx.value.globalCompositeOperation = 'source-over'
}

// Resize canvas to match target container (sibling element)
const resizeCanvas = () => {
    if (!canvas.value) return
    
    // Get the target container (sibling element) to match its dimensions
    const targetContainer = getTargetContainer()
    if (!targetContainer) return
    
    const rect = targetContainer.getBoundingClientRect()
    const parentRect = canvas.value.parentElement ? canvas.value.parentElement.getBoundingClientRect() : { left: 0, top: 0 }
    const dpr = window.devicePixelRatio || 1
    const zoom = props.zoomValue || 1
    
    // Get computed styles to account for border
    // Note: getBoundingClientRect() returns border box (excludes margin, includes border and padding)
    // We subtract borders to get the padding box (content + padding area)
    const computedStyle = window.getComputedStyle(targetContainer)
    
    // Parse border values (accounting for zoom)
    // getBoundingClientRect() includes border, so we need to subtract it to get padding box
    const borderTop = parseFloat(computedStyle.borderTopWidth) / zoom || 0
    const borderRight = parseFloat(computedStyle.borderRightWidth) / zoom || 0
    const borderBottom = parseFloat(computedStyle.borderBottomWidth) / zoom || 0
    const borderLeft = parseFloat(computedStyle.borderLeftWidth) / zoom || 0
    
    // Calculate position relative to parent
    // getBoundingClientRect() returns border box position (excluding margin)
    // We want to position canvas at the content area start (border box + border)
    const x = ((rect.left - parentRect.left) + borderLeft) / zoom
    const y = ((rect.top - parentRect.top) + borderTop) / zoom
    
    // Get the actual (unzoomed) dimensions of the target element covering full content
    const fullWidth = Math.max(rect.width, targetContainer.scrollWidth || 0, targetContainer.offsetWidth || 0)
    const fullHeight = Math.max(rect.height, targetContainer.scrollHeight || 0, targetContainer.offsetHeight || 0)
    // Available viewport height from container top to viewport bottom (unzoomed)
    // to allow drawing over the entire visible screen on short songs without overflowing
    const availableViewportHeight = Math.max(0, (window.innerHeight - rect.top) / zoom)

    const actualWidth = (fullWidth - borderLeft - borderRight) / zoom
    const actualHeight = Math.max((fullHeight - borderTop - borderBottom) / zoom, availableViewportHeight)
    
    // Set canvas internal size (scaled by device pixel ratio for crisp rendering)
    canvas.value.width = actualWidth * dpr
    canvas.value.height = actualHeight * dpr
    
    // Set canvas display size (divide by zoom so when zoom is applied, it matches target visually)
    canvas.value.style.width = actualWidth + 'px'
    canvas.value.style.height = actualHeight + 'px'
    canvas.value.style.left = isMobile(['iPad']) ? (x - 28 / zoom) + 'px' : x + 'px'
    canvas.value.style.top = isMobile() ? (y - 2 / zoom) + 'px' : y + 'px'
    
    // Reset transform and scale context to match device pixel ratio
    if (ctx.value) {
        ctx.value.setTransform(1, 0, 0, 1, 0, 0)
        ctx.value.scale(dpr, dpr)
    }
}

// Get the target container element to overlay
const getTargetContainer = () => {
    // First try the specified target selector
    if (props.targetSelector) {
        const element = document.querySelector(props.targetSelector)
        if (element) {
            return element
        }
    }
    
    // Look for common lyric container selectors as fallback
    const selectors = [
        '.song-viewer',
        '#lyric-container',
        '.lyric-content',
        '[data-lyric-container]',
        '.song-container',
        '.triad-song-container'
    ]
    
    for (const selector of selectors) {
        const element = document.querySelector(selector)
        if (element) {
            return element
        }
    }
    
    // If no specific container found, look for the first sibling with content
    const siblings = canvas.value?.parentElement?.children
    if (siblings) {
        for (let i = 0; i < siblings.length; i++) {
            const sibling = siblings[i]
            if (sibling !== canvas.value && sibling.offsetHeight > 0) {
                return sibling
            }
        }
    }
    
    return null
}

// Get coordinates from event (mouse or touch)
const getCoordinates = (e) => {
    const rect = canvas.value.getBoundingClientRect()
    
    let clientX, clientY
    
    // Handle touch events
    if (e.touches && e.touches.length > 0) {
        clientX = e.touches[0].clientX
        clientY = e.touches[0].clientY
    } else {
        // Handle mouse events
        clientX = e.clientX
        clientY = e.clientY
    }
    
    // Calculate coordinates relative to canvas
    // Account for CSS zoom by dividing by zoom value
    const zoom = props.zoomValue || 1
    const x = (clientX - rect.left) / zoom
    const y = (clientY - rect.top) / zoom
    
    return { x, y }
}

const isClickCandidate = ref(false)
const startClientPos = ref({ x: 0, y: 0 })

// Start drawing
const startDrawing = (e) => {
    if (!isDrawingEnabled.value || !ctx.value) return
    
    e.preventDefault()
    isDrawing.value = true
    isClickCandidate.value = true

    let clientX, clientY
    if (e.touches && e.touches.length > 0) {
        clientX = e.touches[0].clientX
        clientY = e.touches[0].clientY
    } else {
        clientX = e.clientX
        clientY = e.clientY
    }
    startClientPos.value = { x: clientX, y: clientY }
    
    const coords = getCoordinates(e)
    lastX.value = coords.x
    lastY.value = coords.y
    
    ctx.value.beginPath()
    ctx.value.moveTo(lastX.value, lastY.value)
    
    // Initialize current stroke for fade-out tracking
    drawnStrokes.value.push({
        points: [{ x: lastX.value, y: lastY.value }],
        brushSize: brushSize.value,
        brushColor: brushColor.value,
        opacity: 1,
        startTime: Date.now(),
        saved: false // Track if this stroke is saved
    })
}

// Draw
const draw = (e) => {
    if (!isDrawing.value || !isDrawingEnabled.value || !ctx.value) return
    
    e.preventDefault()
    
    const coords = getCoordinates(e)
    
    if (isClickCandidate.value) {
        const dist = Math.hypot(coords.x - lastX.value, coords.y - lastY.value)
        if (dist > 4) {
            isClickCandidate.value = false
        }
    }

    ctx.value.strokeStyle = brushColor.value
    ctx.value.lineWidth = brushSize.value
    
    ctx.value.lineTo(coords.x, coords.y)
    ctx.value.stroke()
    
    // Add point to current stroke for fade-out tracking
    if (drawnStrokes.value.length > 0) {
        const currentStroke = drawnStrokes.value[drawnStrokes.value.length - 1]
        currentStroke.points.push({ x: coords.x, y: coords.y })
    }
    
    lastX.value = coords.x
    lastY.value = coords.y
}

// Stop drawing
const stopDrawing = (e) => {
    if (!isDrawingEnabled.value || !ctx.value) return
    
    e.preventDefault()
    isDrawing.value = false
    ctx.value.beginPath()

    // Handle click on canvas (not a drag/stroke)
    if (isClickCandidate.value) {
        isClickCandidate.value = false
        if (drawnStrokes.value.length > 0) {
            const lastStroke = drawnStrokes.value[drawnStrokes.value.length - 1]
            if (lastStroke.points && lastStroke.points.length <= 2) {
                drawnStrokes.value.pop()
                redrawAllStrokes()
            }
        }

        if (canvas.value) {
            canvas.value.style.pointerEvents = 'none'
            const targetEl = document.elementFromPoint(startClientPos.value.x, startClientPos.value.y)
            canvas.value.style.pointerEvents = ''

            if (targetEl) {
                const commentMark = targetEl.closest('.comment-mark, [data-comment-id], .chord-span')
                if (commentMark) {
                    commentMark.click()
                    return
                }
            }
        }
        return
    }
    
    // Send completed stroke to realtime
    if (drawnStrokes.value.length > 0) {
        const currentStroke = drawnStrokes.value[drawnStrokes.value.length - 1]
        sendStrokeToRealtime({
            saved: currentStroke.saved,
            points: currentStroke.points,
            brushSize: currentStroke.brushSize,
            brushColor: currentStroke.brushColor
        })
        
        // If save draw is enabled, mark current stroke as saved and save to usernotes
        if (saveDrawEnabled.value) {
            currentStroke.saved = true
            saveDrawingToNotes()
            startFadeAnimation()
        } else {
            // If there's a previously saved drawing, keep it persisted but start fade for new strokes
            if (savedDrawingData.value) {
                currentStroke.saved = false
                startFadeAnimation()
            } else {
                startFadeAnimation()
            }
        }
    }
}

// Touch event handlers
const handleTouchStart = (e) => {
    e.preventDefault()
    e.stopPropagation()
    startDrawing(e)
}

const handleTouchMove = (e) => {
    e.preventDefault()
    e.stopPropagation()
    draw(e)
}

const handleTouchEnd = (e) => {
    e.preventDefault()
    e.stopPropagation()
    stopDrawing(e)
}

// Toggle drawing mode
const toggleDrawing = () => {
    isDrawingEnabled.value = !isDrawingEnabled.value
    drawingStore.state.drawingEnabled = isDrawingEnabled.value
    if (isDrawingEnabled.value) {
        emits('drawingEnabled')
    } else {
        emits('drawingDisabled')
    }
}

// Clear canvas
const clearCanvas = async () => {
    if (!ctx.value) return
    
    // Ask user for confirmation
    const confirmed = confirm('Are you sure you want to delete all drawings? This will also delete any saved drawings.')
    
    if (!confirmed) {
        return // User cancelled, do nothing
    }
    
    // Clear saved drawing data
    savedDrawingData.value = null
    
    // Clear the canvas
    ctx.value.clearRect(0, 0, canvas.value.width, canvas.value.height)
    drawnStrokes.value = []
    if (fadeAnimationId.value) {
        cancelAnimationFrame(fadeAnimationId.value)
        fadeAnimationId.value = null
    }
    
    // Delete from localStorage
    try {
        localStorage.removeItem(getStorageKey())
    } catch (error) {
        console.error('Error removing drawing from localStorage:', error)
    }
    // Call API to delete drawing from server
    deleteDrawing()
    
    // Broadcast clear event
    if (channel.value && isConnected.value && appStore.state.enabledRealtime) {
        channel.value.publish('drawing-clear', {
            userId: props.userId,
            timestamp: Date.now()
        })
    }
    
    emits('canvasCleared')
}

const deleteDrawing = async () => {
    try {
        await fetch(`/api/playlist/${props.playlistId}/song/${props.songId}/drawing`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json'
            }
        })
    } catch (error) {
        console.error('Error deleting drawing from API:', error)
    }
}

const saveDrawing = async (drawingData) => {
    try {
        await fetch(`/api/playlist/${props.playlistId}/song/${props.songId}/drawing`, {
            method: 'POST',
            body: JSON.stringify(drawingData),
            headers: {
                'Content-Type': 'application/json'
            }
        })
    } catch (error) {
        console.error('Error saving drawing to API:', error)
    }
}

const saveDrawingOnLocalStorage = (data) => {
    try {
        localStorage.setItem(getStorageKey(), JSON.stringify(data))
    } catch (error) {
        console.warn('LocalStorage save drawing error (persisting in IndexedDB):', error)
    }
    dbService.setItem(getStorageKey(), data).catch(err => {
        console.warn('Error saving drawing to IndexedDB:', err)
    })
}

const loadSavedDrawingFromAPI = async () => {
    if (!props.playlistId || !props.songId || !navigator.onLine) return null
    const drawingUrl = `/api/playlist/${props.playlistId}/song/${props.songId}/drawing`
    if (syncService.hasPending(drawingUrl)) return null
    try {
        const response = await fetch(drawingUrl)
        if (response.ok) {
            const result = await response.json()
            if (result && result.drawing) {
                if (syncService.hasPending(drawingUrl)) return null
                let data = result.drawing
                // Also save to localStorage for faster access next time
                saveDrawingOnLocalStorage(data)
                return data
            }
        }
    } catch (error) {
        console.warn('[SongCanvas] Error loading drawing from API:', error)
    }
    return null
}

// Toggle controls visibility
const toggleControls = () => {
    showControls.value = !showControls.value
    isDrawingEnabled.value = showControls.value
    emits('controlsToggled', showControls.value)
}

// Toggle save draw feature
const toggleSaveDraw = () => {
    saveDrawEnabled.value = !saveDrawEnabled.value
    
    // If disabling save draw, save any unsaved strokes
    if (!saveDrawEnabled.value && drawnStrokes.value.length > 0) {
        saveDrawingToNotes()
    }
    
    emits('saveDrawToggled', saveDrawEnabled.value)
}

// Fade-out animation
const fadeOutAnimation = () => {
    const currentTime = Date.now()
    const fadeDuration = fadeOutDuration.value
    
    // Clear canvas and redraw all strokes with updated opacity
    ctx.value.clearRect(0, 0, canvas.value.width, canvas.value.height)
    
    // First redraw saved drawings at full opacity
    if (savedDrawingData.value) {
        redrawSavedDrawing()
    }
    
    if (drawnStrokes.value.length === 0) {
        fadeAnimationId.value = null
        return
    }
    
    drawnStrokes.value = drawnStrokes.value.filter(stroke => {
        // Skip saved strokes in fade animation - they persist
        if (stroke.saved) {
            // Redraw saved strokes at full opacity
            if (stroke.points.length > 1) {
                ctx.value.beginPath()
                ctx.value.strokeStyle = stroke.brushColor
                ctx.value.lineWidth = stroke.brushSize
                ctx.value.globalAlpha = 1
                
                ctx.value.moveTo(stroke.points[0].x, stroke.points[0].y)
                for (let i = 1; i < stroke.points.length; i++) {
                    ctx.value.lineTo(stroke.points[i].x, stroke.points[i].y)
                }
                ctx.value.stroke()
            }
            return true // Keep saved strokes
        }
        
        const elapsed = currentTime - stroke.startTime
        if (elapsed >= fadeDuration) {
            return false // Remove completely faded strokes
        }
        
        // Calculate opacity based on elapsed time
        stroke.opacity = 1 - (elapsed / fadeDuration)
        
        // Redraw stroke with current opacity
        if (stroke.points.length > 1) {
            ctx.value.beginPath()
            ctx.value.strokeStyle = stroke.brushColor
            ctx.value.lineWidth = stroke.brushSize
            ctx.value.globalAlpha = stroke.opacity
            
            ctx.value.moveTo(stroke.points[0].x, stroke.points[0].y)
            for (let i = 1; i < stroke.points.length; i++) {
                ctx.value.lineTo(stroke.points[i].x, stroke.points[i].y)
            }
            ctx.value.stroke()
        }
        
        return true
    })
    
    // Reset global alpha
    ctx.value.globalAlpha = 1
    
    // Continue animation if there are still strokes to fade
    if (drawnStrokes.value.length > 0) {
        fadeAnimationId.value = requestAnimationFrame(fadeOutAnimation)
    } else {
        fadeAnimationId.value = null
    }
}

// Save drawing to localStorage and API
const saveDrawingToNotes = async () => {
    if (drawnStrokes.value.length === 0) return
    
    // Get newly saved strokes (current strokes that are marked as saved)
    const newlySavedStrokes = drawnStrokes.value.filter(stroke => stroke.saved)
    
    if (newlySavedStrokes.length === 0) return
    
    // Initialize savedDrawingData if it doesn't exist
    if (!savedDrawingData.value || savedDrawingData.value.length === 0) {
        savedDrawingData.value = []
    }
    
    // Merge new saved strokes with existing saved drawing data
    // Create a copy of savedDrawingData to avoid modifying it directly
    const existingSavedStrokes = JSON.parse(JSON.stringify(savedDrawingData.value))
    
    
    // Add new saved strokes that aren't already in the saved drawing
    newlySavedStrokes.forEach(newStroke => {
        // Check if this stroke already exists in saved data
        const strokeExists = existingSavedStrokes.some(existingStroke => 
            JSON.stringify(existingStroke.points) === JSON.stringify(newStroke.points) &&
            existingStroke.brushSize === newStroke.brushSize &&
            existingStroke.brushColor === newStroke.brushColor
        )
        
        if (!strokeExists) {
            existingSavedStrokes.push(JSON.parse(JSON.stringify(newStroke)))
        }
    })
    
    // Update savedDrawingData with merged strokes
    savedDrawingData.value = existingSavedStrokes
    
    const drawingData = {
        strokes: savedDrawingData.value,
        timestamp: Date.now()
    }
    
    // Save to localStorage
    saveDrawingOnLocalStorage(drawingData)
    
    // Save to API endpoint
    saveDrawing(drawingData)
}

// Redraw saved drawing
const redrawSavedDrawing = () => {
    if (!ctx.value) return
    if (!savedDrawingData.value || savedDrawingData.value.length === 0) return
    
    savedDrawingData.value.forEach(stroke => {
        if (stroke.points.length > 1) {
            ctx.value.beginPath()
            ctx.value.strokeStyle = stroke.brushColor
            ctx.value.lineWidth = stroke.brushSize
            ctx.value.globalAlpha = 1
            
            ctx.value.moveTo(stroke.points[0].x, stroke.points[0].y)
            for (let i = 1; i < stroke.points.length; i++) {
                ctx.value.lineTo(stroke.points[i].x, stroke.points[i].y)
            }
            ctx.value.stroke()
        }
    })
    
    ctx.value.globalAlpha = 1
}

// Redraw all strokes (both saved and non-saved)
const redrawAllStrokes = () => {
    if (!ctx.value) return
    
    // Clear canvas first
    ctx.value.clearRect(0, 0, canvas.value.width, canvas.value.height)
    
    // Redraw all strokes from drawnStrokes array
    drawnStrokes.value.forEach(stroke => {
        if (stroke.points && stroke.points.length > 1) {
            ctx.value.beginPath()
            ctx.value.strokeStyle = stroke.brushColor
            ctx.value.lineWidth = stroke.brushSize
            ctx.value.globalAlpha = stroke.opacity !== undefined ? stroke.opacity : 1
            
            ctx.value.moveTo(stroke.points[0].x, stroke.points[0].y)
            for (let i = 1; i < stroke.points.length; i++) {
                ctx.value.lineTo(stroke.points[i].x, stroke.points[i].y)
            }
            ctx.value.stroke()
        }
    })
    
    // Reset global alpha
    ctx.value.globalAlpha = 1
}

// Load saved drawing
const loadSavedDrawing = async (drawingData = null) => {
    let data = drawingData
    
    if (!data) {
        // Try loading from user-scoped localStorage first
        try {
            const stored = localStorage.getItem(getStorageKey())
            if (stored) {
                data = JSON.parse(stored)
            }
        } catch (error) {
            console.error('Error loading drawing from localStorage:', error)
        }

        // Check legacy localStorage if user-scoped is empty
        if (!data) {
            try {
                const legacyStored = localStorage.getItem(getLegacyStorageKey())
                if (legacyStored) {
                    data = JSON.parse(legacyStored)
                    if (data) {
                        saveDrawingOnLocalStorage(data)
                    }
                }
            } catch (error) {}
        }

        // If not in localStorage, try user-scoped IndexedDB
        if (!data) {
            try {
                const idbData = await dbService.getItem(getStorageKey())
                if (idbData) {
                    data = idbData
                }
            } catch (error) {
                console.warn('Error loading drawing from IndexedDB:', error)
            }
        }

        // Check legacy IndexedDB if user-scoped is empty
        if (!data) {
            try {
                const legacyIdbData = await dbService.getItem(getLegacyStorageKey())
                if (legacyIdbData) {
                    data = legacyIdbData
                    if (data) {
                        dbService.setItem(getStorageKey(), data)
                    }
                }
            } catch (error) {}
        }
        
        // If not in localStorage / IndexedDB and online, try loading from API
        if (!data && navigator.onLine) {
            data = await loadSavedDrawingFromAPI()
        }
    }
    
    if (!data || !data.strokes || data.strokes.length === 0) return
    
    savedDrawingData.value = JSON.parse(JSON.stringify(data.strokes))
    
    // Add loaded strokes to drawnStrokes and mark them as saved
    savedDrawingData.value.forEach(stroke => {
        stroke.saved = true
        stroke.opacity = 1
        stroke.startTime = data.timestamp || Date.now()
        drawnStrokes.value.push(stroke)
    })
    
    // Redraw the saved drawing
    if (ctx.value) {
        redrawSavedDrawing()
    }
}

// Load saved drawing once the song content is ready and the canvas is sized.
// Falls back to the container resize observer if content is still loading.
const ensureDrawingLoaded = () => {
    if (drawingLoaded.value) return
    if (!props.contentReady) return
    
    drawingLoaded.value = true
    nextTick(() => {
        resizeCanvas()
        loadSavedDrawing()
    })
}

// Start fade-out animation
const startFadeAnimation = () => {
    if (fadeAnimationId.value) {
        cancelAnimationFrame(fadeAnimationId.value)
    }
    fadeAnimationId.value = requestAnimationFrame(fadeOutAnimation)
}

// Handle window resize
const handleResize = () => {
    resizeCanvas()
    // Redraw all strokes after resize since resizeCanvas clears the canvas
    nextTick(() => {
        redrawAllStrokes()
    })
}

// Observer for target container changes
let resizeObserver = null

// Setup observer to watch for target container changes
const setupObserver = () => {
    if (resizeObserver) {
        resizeObserver.disconnect()
    }
    
    const targetContainer = getTargetContainer()
    if (targetContainer) {
        resizeObserver = new ResizeObserver(() => {
            resizeCanvas()
            // Redraw all strokes after resize since resizeCanvas clears the canvas
            nextTick(() => {
                redrawAllStrokes()
            })
            // Once the container is laid out at its final size, load the saved drawing
            ensureDrawingLoaded()
        })
        resizeObserver.observe(targetContainer)
    }
}

// Manual method to update canvas position
const updatePosition = () => {
    resizeCanvas()
    // Redraw all strokes after resize since resizeCanvas clears the canvas
    nextTick(() => {
        redrawAllStrokes()
    })
}

// Watch for prop changes
watch(() => props.enabled, (newValue) => {
    isDrawingEnabled.value = newValue
})

watch(() => props.brushSize, (newValue) => {
    brushSize.value = newValue
})

watch(() => props.brushColor, (newValue) => {
    brushColor.value = newValue
})

watch(() => props.fadeOutDuration, (newValue) => {
    fadeOutDuration.value = newValue
})

watch(() => props.contentReady, (newValue) => {
    if (newValue) {
        setupObserver()
        resizeCanvas()
        ensureDrawingLoaded()
    }
})

watch(() => props.songId, async (newSongId, oldSongId) => {
    if (newSongId !== oldSongId) {
        drawnStrokes.value = []
        savedDrawingData.value = null
        drawingLoaded.value = false
        if (ctx.value && canvas.value) {
            ctx.value.clearRect(0, 0, canvas.value.width, canvas.value.height)
        }
        if (fadeAnimationId.value) {
            cancelAnimationFrame(fadeAnimationId.value)
            fadeAnimationId.value = null
        }
        await nextTick()
        setupObserver()
        resizeCanvas()
        ensureDrawingLoaded()
    }
})

// Lifecycle
onMounted(() => {
    nextTick(() => {
        initCanvas()
        setupObserver()
        setupRealtimeChannel()
        window.addEventListener('resize', handleResize)
        
        // Add passive touch event listeners for better mobile performance
        if (canvas.value) {
            canvas.value.addEventListener('touchstart', handleTouchStart, { passive: false })
            canvas.value.addEventListener('touchmove', handleTouchMove, { passive: false })
            canvas.value.addEventListener('touchend', handleTouchEnd, { passive: false })
            canvas.value.addEventListener('touchcancel', handleTouchEnd, { passive: false })
        }
        
        // Load saved drawing once the song content is ready
        ensureDrawingLoaded()
    })
})

onUnmounted(() => {
    window.removeEventListener('resize', handleResize)
    if (resizeObserver) {
        resizeObserver.disconnect()
    }
    if (fadeAnimationId.value) {
        cancelAnimationFrame(fadeAnimationId.value)
    }
    
    // Cleanup realtime channel
    if (channel.value) {
        realtimeService.unsubscribe(`drawing-room-${props.roomId}`)
    }
    
    // Remove touch event listeners
    if (canvas.value) {
        canvas.value.removeEventListener('touchstart', handleTouchStart)
        canvas.value.removeEventListener('touchmove', handleTouchMove)
        canvas.value.removeEventListener('touchend', handleTouchEnd)
        canvas.value.removeEventListener('touchcancel', handleTouchEnd)
    }
})
// const addUserNotes = () => {
//     emits('addUserNotes')
// }

// Color picker methods
const toggleColorPicker = () => {
    if (colorPicker.value) {
        colorPicker.value.toggle()
    }
}

const handleColorChange = (color) => {
    brushColor.value = color
}
watch(() => props.zoomValue, () => {   
    nextTick( () => {
        resizeCanvas()
        // Redraw all strokes (both saved and non-saved) after resize
        redrawAllStrokes()
    })
})

// Expose methods
defineExpose({
    toggleDrawing,
    toggleControls,
    toggleSaveDraw,
    clearCanvas,
    updatePosition,
    isDrawingEnabled: () => isDrawingEnabled.value,
    isSaveDrawEnabled: () => saveDrawEnabled.value,
    setBrushSize: (size) => { brushSize.value = size },
    setBrushColor: (color) => { brushColor.value = color },
    loadSavedDrawing
})
</script>

<style scoped>
canvas {
    touch-action: none;
}

/* Custom range slider styles for slate theme */
.slider {
    -webkit-appearance: none;
    appearance: none;
    background: transparent;
    cursor: pointer;
}

.slider::-webkit-slider-track {
    background: #475569; /* slate-600 */
    height: 4px;
    border-radius: 2px;
}

.slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    background: #8b5cf6; /* violet-500 */
    height: 16px;
    width: 16px;
    border-radius: 50%;
    cursor: pointer;
    border: 2px solid #1e293b; /* slate-800 */
}

.slider::-webkit-slider-thumb:hover {
    background: #7c3aed; /* violet-600 */
}

.slider::-moz-range-track {
    background: #475569; /* slate-600 */
    height: 4px;
    border-radius: 2px;
    border: none;
}

.slider::-moz-range-thumb {
    background: #8b5cf6; /* violet-500 */
    height: 16px;
    width: 16px;
    border-radius: 50%;
    cursor: pointer;
    border: 2px solid #1e293b; /* slate-800 */
}

.slider::-moz-range-thumb:hover {
    background: #7c3aed; /* violet-600 */
}
</style>